import fallbackMock from "../thread-design-system/data/mock-data.json";
import type {
  BridgeMap,
  ThreadAsset,
  ThreadAssetSource,
  ThreadAssetType,
  ThreadCluster,
  ThreadColumn,
  ThreadFamily,
  ThreadHealth,
  ThreadRow,
} from "../thread-design-system";
import type {
  ArtifactRecord,
  GraphResponse,
  PlotRecord,
  ReportRecord,
  ScenarioResponse,
  TableRecord,
  TableRowRecord,
} from "../../pages/v2-workbench/types";
import type { RunTableData } from "../../api/runTable";
import type { SavedPlot } from "../../api/plots";
import { enrichV3Assets, enrichV3Clusters, enrichV3GraphEdges } from "./richAssets";
import { applyProvenanceMetadata, buildV3ProvenanceGraph } from "./provenance";
import { derivePivotCandidates } from "./pivots";
import type { ProjectSummary, V3WorkbenchData } from "./types";

type MockShape = {
  families?: Record<string, ThreadFamily>;
  bridges?: BridgeMap;
  clusters?: ThreadCluster[];
  edges?: Array<readonly [string, string]>;
  rows?: ThreadRow[];
  assets?: ThreadAsset[];
};

const mock = fallbackMock as unknown as MockShape;
const V3_PERCEPTUAL_ROW_BUDGET = 1_200;

const QUANTITY_FIELDS = new Set([
  "cl",
  "cd",
  "cm",
  "ld",
  "c_l",
  "c_d",
  "c_m",
  "cy",
  "cfx",
  "cfy",
  "cfz",
  "cl_roll",
  "cm_pitch",
  "cn_yaw",
  "clpressure",
  "cdpressure",
  "cfxpressure",
  "cfypressure",
  "cfzpressure",
  "cmxpressure",
  "cmypressure",
  "cmzpressure",
  "clskinfriction",
  "cdskinfriction",
  "cfxskinfriction",
  "cfyskinfriction",
  "cfzskinfriction",
  "cmxskinfriction",
  "cmyskinfriction",
  "cmzskinfriction",
  "cl_alpha",
  "cm_alpha",
  "omega_sp",
  "cl_alpha_avl",
  "cm_alpha_avl",
  "cy_beta_avl",
  "cl_beta_avl",
  "cn_beta_avl",
  "C_L",
  "C_D",
  "C_m",
  "alpha",
  "mach",
  "re",
  "reynolds",
]);

const CONDITION_FIELDS = new Set(["alpha", "beta", "mach", "re", "reynolds", "rpm", "thrust", "elevator", "aileron", "condition_key", "conditionKey"]);

export function normalizeFieldId(field: string): string {
  const mapped: Record<string, string> = {
    C_L: "cl",
    CL: "cl",
    C_D: "cd",
    CD: "cd",
    C_m: "cm",
    CM: "cm",
    CMy: "cm",
    cmy: "cm",
    "L/D": "ld",
    alpha_deg: "alpha",
    alpha_degrees: "alpha",
    beta_deg: "beta",
    beta_degrees: "beta",
    mach_number: "mach",
    reynolds_number: "re",
    Re_L: "re",
    reynolds: "re",
    sample_id: "runId",
  };
  return mapped[field] ?? field;
}

function stringValue(value: unknown, fallback: string): string {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function healthFromStatus(value: unknown): ThreadHealth {
  if (value === "fresh" || value === "stale" || value === "warn" || value === "unknown") return value;
  if (typeof value === "string") {
    const lowered = value.toLowerCase();
    if (lowered.includes("stale") || lowered.includes("fail") || lowered.includes("error")) return "stale";
    if (lowered.includes("warn") || lowered.includes("queued")) return "warn";
    if (lowered.includes("complete") || lowered.includes("fresh") || lowered.includes("active")) return "fresh";
  }
  return "unknown";
}

function columnKind(field: string): ThreadColumn["kind"] {
  const normalized = normalizeFieldId(field);
  const normalizedLower = normalized.toLowerCase();
  if (normalized === "runId" || normalized.endsWith("_id")) return "id";
  if (normalized === "family") return "family";
  if (CONDITION_FIELDS.has(normalized) || CONDITION_FIELDS.has(normalizedLower)) return "condition";
  if (QUANTITY_FIELDS.has(field) || QUANTITY_FIELDS.has(normalized) || QUANTITY_FIELDS.has(normalizedLower)) return "quantity";
  return "metadata";
}

export function adaptColumns(columns: Array<{ field: string; headerName?: string }>): ThreadColumn[] {
  return columns.filter((column) => column.field !== "_lineage" && column.field !== "_cell_lineage").map((column) => {
    const id = normalizeFieldId(column.field);
    return {
      id,
      label: column.headerName ?? column.field,
      kind: columnKind(column.field),
    };
  });
}

function rowIdFromRecord(row: Record<string, unknown>, index: number): string {
  return stringValue(
    row.runId ??
      row.name ??
      row.case_id ??
      row.flow360_case_id ??
      row.sample_id ??
      row.flat_view_row_id ??
      row.joined_row_id ??
      row.analysis_row_id ??
      row.row_id ??
      row._row_id ??
      row.id,
    `row-${index + 1}`,
  );
}

export function adaptRows(rows: Record<string, unknown>[]): ThreadRow[] {
  return rows.map((row, index) => {
    const runId = rowIdFromRecord(row, index);
    const family = stringValue(row.family ?? row.tsangpo_campaign_id ?? row.sweep_group ?? row.source_tool ?? row.model_family ?? row.model ?? row.solver, "unknown");
    const adapted: ThreadRow = {
      runId,
      family,
      alpha: numberValue(row.alpha ?? row.alpha_deg),
      beta: numberValue(row.beta ?? row.beta_deg),
      mach: numberValue(row.mach),
      re: numberValue(row.re ?? row.reynolds),
      conditionKey: stringValue(row.condition_key ?? row.conditionKey, ""),
      mesh: stringValue(row.mesh ?? row.mesh_id ?? row.tsangpo_geometry_variant_label ?? row.tsangpo_campaign_id ?? row.sweep_group, ""),
      script: stringValue(row.script ?? row.operation ?? row.source_id ?? row.flow360_folder_name, ""),
      design: stringValue(row.design ?? row.geometry_hash ?? row.geometry_id ?? row.tsangpo_family ?? row.tsangpo_phase, ""),
      status: healthFromStatus(row.status),
    };

    for (const [key, value] of Object.entries(row)) {
      if (key.startsWith("_") && key !== "_node_id" && key !== "_lineage" && key !== "_cell_lineage") continue;
      const normalized = normalizeFieldId(key);
      if (normalized in adapted && adapted[normalized] !== "" && adapted[normalized] != null) continue;
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        value == null ||
        key === "lineage" ||
        key === "_lineage" ||
        key === "_cell_lineage"
      ) {
        adapted[normalized] = value;
      }
    }

    if (!adapted.conditionKey) {
      const parts = [
        adapted.design,
        adapted.family,
        adapted.alpha != null ? `a=${adapted.alpha}` : "",
        adapted.mach != null ? `m=${adapted.mach}` : "",
        adapted.re != null ? `re=${adapted.re}` : "",
      ].filter(Boolean);
      adapted.conditionKey = parts.join("|");
    }

    return adapted;
  });
}

export function adaptRunTable(runTable: RunTableData | null): {
  rows: ThreadRow[];
  columns: ThreadColumn[];
} {
  if (!runTable?.rows?.length || !runTable.columns?.length) return { rows: [], columns: [] };
  return {
    rows: adaptRows(runTable.rows),
    columns: adaptColumns(runTable.columns),
  };
}

function tableRows(table: TableRecord): Record<string, unknown>[] {
  return table.rows.map((row: TableRowRecord) => row as Record<string, unknown>);
}

export function adaptScenarioTable(scenario: ScenarioResponse | null): {
  rows: ThreadRow[];
  columns: ThreadColumn[];
} {
  const table =
    scenario?.tables.find((item) => item.primary) ??
    scenario?.tables[0] ??
    null;
  if (!table) return { rows: [], columns: [] };
  return {
    rows: adaptRows(tableRows(table)),
    columns: adaptColumns(table.columns),
  };
}

function sourceForArtifact(artifact: ArtifactRecord | PlotRecord | ReportRecord): ThreadAssetSource {
  const kind = artifact.kind.toLowerCase();
  const category = "category" in artifact && typeof artifact.category === "string" ? artifact.category.toLowerCase() : "";
  if (kind.includes("sheet") || kind.includes("spreadsheet") || kind.includes("workbook") || category.includes("spreadsheet")) return kind.includes("google") ? "google-sheets" : "excel";
  if (kind.includes("slide")) return "google-slides";
  if (kind.includes("presentation") || kind.includes("deck")) return "powerpoint";
  if (kind.includes("slack")) return "slack";
  if (kind.includes("email")) return "email";
  if (kind.includes("pdf") || kind.includes("report")) return "pdf";
  if (kind.includes("uvf") || kind.includes("flowfield")) return "uvf";
  return "thread";
}

function typeForArtifact(kind: string, category?: string): ThreadAssetType {
  const value = `${kind} ${category ?? ""}`.toLowerCase();
  if (value.includes("sheet") || value.includes("spreadsheet") || value.includes("workbook") || value.includes("excel")) return "spreadsheet";
  if (value.includes("plot")) return "plot";
  if (value.includes("deck") || value.includes("presentation") || value.includes("slide")) return "deck";
  if (value.includes("chat") || value.includes("conversation") || value.includes("slack")) return "chat";
  if (value.includes("email")) return "email";
  if (value.includes("report") || value.includes("pdf")) return "report";
  if (value.includes("notebook")) return "notebook";
  if (value.includes("uvf") || value.includes("flowfield")) return "uvf";
  if (value.includes("table")) return "table-view";
  if (value.includes("correlogram")) return "correlogram-view";
  return "report";
}

function rowsForArtifact(
  artifactId: string,
  scenario: ScenarioResponse | null,
  rows: readonly ThreadRow[],
): string[] {
  const refs = new Set<string>();

  for (const table of scenario?.tables ?? []) {
    for (const row of table.rows) {
      const rowId = rowIdFromRecord(row as Record<string, unknown>, 0);
      if (row._node_id === artifactId) refs.add(rowId);
      for (const lineage of Object.values(row._cell_lineage ?? {})) {
        if (lineage?.artifact_id === artifactId) refs.add(rowId);
      }
    }
  }

  for (const plot of scenario?.plots ?? []) {
    if (plot.artifact_id !== artifactId) continue;
    for (const point of plot.points) {
      if (point.flat_view_row_id) refs.add(point.flat_view_row_id);
      for (const sourceRowId of point.source_row_ids ?? []) refs.add(sourceRowId);
    }
  }

  if (refs.size === 0 && rows.length) {
    for (const row of rows.slice(0, Math.min(rows.length, 80))) refs.add(row.runId);
  }

  return [...refs];
}

function upstreamClusterRefs(operations: readonly { id: string }[], clusterHint: string): string[] {
  return [...new Set([clusterHint, "operation", ...operations.map((operation) => operation.id)])];
}

export function adaptAssets(scenario: ScenarioResponse | null, rows: readonly ThreadRow[]): ThreadAsset[] {
  const assets: ThreadAsset[] = [];

  for (const artifact of scenario?.artifacts ?? []) {
    const type = typeForArtifact(artifact.kind, artifact.category);
    assets.push({
      id: artifact.id,
      type,
      source: sourceForArtifact(artifact),
      label: artifact.label,
      sub: `${artifact.kind}${artifact.branch_name ? ` · ${artifact.branch_name}` : ""}`,
      updated: artifact.content_hash ? "tracked" : "live",
      health: "fresh",
      summary: artifact.description ?? undefined,
      refs: {
        rows: rowsForArtifact(artifact.id, scenario, rows),
        upstream: upstreamClusterRefs(artifact.linked_operations, artifact.kind),
      },
      preview:
        type === "table-view"
          ? { kind: "table-view", cols: [], rowCount: rows.length }
          : type === "report"
            ? { kind: "report", title: artifact.label, abstract: artifact.description ?? undefined }
            : undefined,
    });
  }

  for (const plot of scenario?.plots ?? []) {
    assets.push({
      id: plot.artifact_id,
      type: "plot",
      source: "thread",
      label: plot.label,
      sub: `${plot.kind} · ${plot.points.length} pts`,
      updated: "live",
      health: "fresh",
      summary: plot.description ?? undefined,
      refs: { rows: rowsForArtifact(plot.artifact_id, scenario, rows), upstream: ["saved_plot", "plot", "operation"] },
      preview: { kind: "plot", seriesCount: plot.data.length },
    });
  }

  for (const report of scenario?.reports ?? []) {
    assets.push({
      id: report.artifact_id,
      type: "report",
      source: "thread",
      label: report.label,
      sub: report.kind,
      updated: "live",
      health: "fresh",
      summary: report.description ?? report.markdown?.slice(0, 180),
      refs: { rows: rows.slice(0, 80).map((row) => row.runId), upstream: ["report", "operation"] },
      preview: { kind: "report", title: report.label, abstract: report.description ?? undefined },
    });
  }

  const deduped = new Map<string, ThreadAsset>();
  for (const asset of assets) deduped.set(asset.id, asset);
  return [...deduped.values()];
}

function metadataString(metadata: Record<string, unknown> | undefined, key: string): string | null {
  const value = metadata?.[key];
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (value && typeof value === "object" && key === "code_ref") {
    const record = value as Record<string, unknown>;
    for (const refKey of ["relative_path", "file", "url"]) {
      const refValue = record[refKey];
      if (typeof refValue === "string" && refValue.trim()) return refValue;
    }
  }
  return null;
}

function savedPlotRows(plot: SavedPlot, rows: readonly ThreadRow[]): string[] {
  const refs = new Set<string>();

  for (const point of plot.data_snapshot ?? []) {
    const id = rowIdFromRecord(point, 0);
    if (id) refs.add(id);
    for (const key of ["flat_view_row_id", "joined_row_id", "analysis_row_id", "row_id", "runId", "name"]) {
      const value = point[key];
      if (typeof value === "string" && value.trim()) refs.add(value);
    }
  }

  const sourceRunPaths = plot.metadata?.source_run_paths;
  if (Array.isArray(sourceRunPaths)) {
    for (const value of sourceRunPaths) {
      if (typeof value === "string" && value.trim()) refs.add(value);
    }
  }

  if (refs.size === 0 && rows.length) {
    for (const row of rows.slice(0, Math.min(rows.length, 80))) refs.add(row.runId);
  }

  return [...refs];
}

function savedPlotImageKey(plot: SavedPlot): string | null {
  if (!plot.image_url) return null;
  try {
    const url = new URL(plot.image_url);
    return `${url.origin}${url.pathname}`;
  } catch {
    return plot.image_url.split("?")[0] || plot.image_url;
  }
}

export function adaptSavedPlots(savedPlots: readonly SavedPlot[] | undefined, rows: readonly ThreadRow[]): ThreadAsset[] {
  if (!savedPlots?.length) return [];

  const nativeImageKeys = new Set(
    savedPlots
      .filter((plot) => plot.chart_type !== "image" && plot.config?.engine !== "image")
      .map(savedPlotImageKey)
      .filter((key): key is string => Boolean(key)),
  );

  return savedPlots
    .filter((plot) => plot.metadata?.collection_role !== "member")
    .filter((plot) => {
      if (plot.chart_type !== "image" && plot.config?.engine !== "image") return true;
      const imageKey = savedPlotImageKey(plot);
      return !imageKey || !nativeImageKeys.has(imageKey);
    })
    .map((plot): ThreadAsset => {
      const metadata = plot.metadata ?? {};
      const contentHash = metadataString(metadata, "content_hash");
      const hashId = metadataString(metadata, "hash_id");
      const captureMode = metadataString(metadata, "capture_mode");
      const source = captureMode === "thread_sdk" || metadataString(metadata, "source") === "python" ? "python" : "thread";
      const chartLabel = plot.chart_type || metadataString(metadata, "kind") || "plot";
      const pointCount = Array.isArray(plot.data_snapshot) ? plot.data_snapshot.length : 0;
      const native = pointCount > 0 && chartLabel !== "image" && plot.config?.engine !== "image";
      const codeRef = metadataString(metadata, "code_ref") ?? metadataString(metadata, "source_file");
      const summaryParts = [
        codeRef ? `Generated by ${codeRef}` : null,
        hashId ? `Recognizer tag ${hashId}` : null,
        contentHash ? `Content hash ${contentHash.slice(0, 12)}` : null,
      ].filter(Boolean);

      return {
        id: plot.id,
        provenanceNodeId: plot.id,
        type: "plot",
        source,
        label: plot.title || plot.path || "Saved plot",
        sub: `${chartLabel}${pointCount ? ` · ${pointCount} pts` : ""}${native ? " · native" : ""}${plot.image_url ? " · PNG" : ""}`,
        updated: contentHash || plot.image_url ? "tracked" : "live",
        health: "fresh",
        external: plot.image_url ?? undefined,
        summary: summaryParts.join(". ") || plot.path || undefined,
        refs: {
          rows: savedPlotRows(plot, rows),
          upstream: ["saved_plot", "plot", "thread_sdk", ...plot.source_node_ids],
        },
        preview: {
          kind: "plot",
          seriesCount: Array.isArray(plot.config?.series) ? plot.config.series.length : 1,
        },
      };
    });
}

function clusterLabel(kind: string): string {
  return kind
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function adaptClusters(graph: GraphResponse | null, scenario: ScenarioResponse | null): ThreadCluster[] {
  if (graph?.nodes?.length) {
    const counts = new Map<string, number>();
    for (const node of graph.nodes) {
      const key = node.node_type === "operation" ? "operation" : node.kind || "artifact";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].map(([type, nodes], index) => ({
      id: type,
      type,
      label: clusterLabel(type),
      nodes,
      x: 90 + index * 120,
      y: 120 + (index % 3) * 84,
      health: "fresh",
    }));
  }

  if (scenario) {
    return [
      { id: "source", type: "source", label: "Sources", nodes: scenario.summary.source_count },
      { id: "table", type: "table", label: "Tables", nodes: scenario.summary.table_count },
      { id: "plot", type: "plot", label: "Plots", nodes: scenario.summary.plot_count },
      { id: "report", type: "report", label: "Reports", nodes: scenario.summary.report_count },
    ].filter((cluster) => cluster.nodes > 0);
  }

  return mock.clusters ?? [];
}

export function adaptGraphEdges(graph: GraphResponse | null, clusters: readonly ThreadCluster[]): Array<readonly [string, string]> {
  if (!graph?.edges?.length) return mock.edges ?? [];
  const nodeToCluster = new Map<string, string>();
  for (const node of graph.nodes) {
    nodeToCluster.set(node.id, node.node_type === "operation" ? "operation" : node.kind || "artifact");
  }
  const edges = new Set<string>();
  for (const edge of graph.edges) {
    const source = nodeToCluster.get(edge.source);
    const target = nodeToCluster.get(edge.target);
    if (!source || !target || source === target) continue;
    edges.add(`${source}|${target}`);
  }
  const clusterIds = new Set(clusters.map((cluster) => cluster.id));
  return [...edges]
    .map((edge) => edge.split("|") as [string, string])
    .filter(([source, target]) => clusterIds.has(source) && clusterIds.has(target));
}

export function familiesFromRows(rows: readonly ThreadRow[]): Record<string, ThreadFamily> {
  const families: Record<string, ThreadFamily> = { ...(mock.families ?? {}) };
  for (const row of rows) {
    if (families[row.family]) continue;
    families[row.family] = {
      label: clusterLabel(row.family),
      short: row.family.slice(0, 10).toUpperCase(),
      tone: "fresh",
    };
  }
  return families;
}

export function bridgesForRows(rows: readonly ThreadRow[]): BridgeMap {
  const bridges: BridgeMap = { ...(mock.bridges ?? {}) };
  const families = [...new Set(rows.map((row) => row.family))].filter(Boolean);
  for (const quantity of ["cl", "cd", "cm", "ld"]) {
    if (bridges[quantity]?.length) continue;
    if (families.length >= 2) bridges[quantity] = [[families[0]!, families[1]!]];
  }
  return bridges;
}

export function buildV3Data(input: {
  project: ProjectSummary | null;
  branch: string;
  scenario: ScenarioResponse | null;
  graph: GraphResponse | null;
  runTable: RunTableData | null;
  savedPlots?: readonly SavedPlot[];
  rowBudget?: number;
  errors?: string[];
  allowMockFallback?: boolean;
}): V3WorkbenchData {
  const scenarioTable = adaptScenarioTable(input.scenario);
  const runTable = adaptRunTable(input.runTable);
  const allowMockFallback = input.allowMockFallback ?? true;
  const emptyColumns =
    input.scenario?.tables[0]?.columns ? adaptColumns(input.scenario.tables[0].columns) :
      input.runTable?.columns ? adaptColumns(input.runTable.columns) :
        [];
  const chosen = scenarioTable.rows.length ? scenarioTable : runTable.rows.length ? runTable : allowMockFallback ? {
    rows: mock.rows ?? [],
    columns: adaptColumns(Object.keys((mock.rows ?? [])[0] ?? {}).map((field) => ({ field, headerName: field }))),
  } : {
    rows: [],
    columns: emptyColumns,
  };
  const source = scenarioTable.rows.length ? "scenario" : runTable.rows.length ? "run-table" : allowMockFallback ? "mock-fallback" : input.scenario ? "scenario" : input.runTable ? "run-table" : "mock-fallback";
  const totalRowCount = source === "run-table" ? input.runTable?.total ?? chosen.rows.length : chosen.rows.length;
  const rowBudget = Math.max(1, Math.min(input.rowBudget ?? V3_PERCEPTUAL_ROW_BUDGET, totalRowCount));
  const rows = chosen.rows.slice(0, rowBudget);
  const clusters = adaptClusters(input.graph, input.scenario);
  const adaptedAssets = [...adaptAssets(input.scenario, rows), ...adaptSavedPlots(input.savedPlots, rows)];
  const assets = allowMockFallback ? enrichV3Assets(adaptedAssets, rows, chosen.columns) : adaptedAssets;
  const provenanceAssets = assets.length ? assets : allowMockFallback ? mock.assets ?? [] : [];
  const enrichedClusters = enrichV3Clusters(clusters, assets);
  const graphEdges = input.graph?.edges?.length || allowMockFallback ? adaptGraphEdges(input.graph, enrichedClusters) : [];
  const provenance = buildV3ProvenanceGraph({
    rows,
    totalRowCount,
    columns: chosen.columns,
    assets: provenanceAssets,
    clusters: enrichedClusters,
    scenario: input.scenario,
    graph: input.graph,
  });
  const withProvenance = applyProvenanceMetadata(rows, provenanceAssets, provenance);
  const pivotViews = derivePivotCandidates(withProvenance.rows, chosen.columns, provenance);

  return {
    project: input.project,
    branch: input.branch,
    rows: withProvenance.rows,
    totalRowCount,
    columns: chosen.columns,
    families: familiesFromRows(rows),
    bridges: bridgesForRows(rows),
    assets: withProvenance.assets.length ? withProvenance.assets : allowMockFallback ? enrichV3Assets(mock.assets ?? [], withProvenance.rows, chosen.columns) : [],
    clusters: enrichedClusters,
    graphEdges: enrichV3GraphEdges(graphEdges, enrichedClusters),
    provenance,
    pivotViews,
    source,
    errors: input.errors ?? [],
  };
}
